import { FC, useEffect, useRef, useState } from 'react';
import LocalIcon from '../../../ui/LocalIcon';
import { black } from '../../../utils/style/colors';
import PageCKEditor from '../../FileManager/ItemsViewer/Quill/PageCkEditor';
import styled from 'styled-components';
import { Banner, Content } from '../styles';

const AudioBanner = styled(Banner)`
  height: 15rem !important;
  display: grid;
  grid-template-columns: repeat(5, minmax(auto, 1fr));
  grid-template-rows: repeat(1, minmax(auto, 1fr));

  & > img {
    height: 100%;
    width: 100%;
    grid-column: 1/2;
    grid-row: 1/-1;
    padding: 0.75rem;
    border-radius: 1.5rem;
    object-fit: cover;
  }

  & > div {
    grid-column: 2/-1;
    grid-row: 1/-1;
    display: flex;
    flex-direction: column;
    padding: 1rem 2.5rem;
  }

  @media screen and (max-width: 800px) {
    min-height: 26rem;
    grid-template-columns: repeat(5, minmax(auto, 1fr));
    grid-template-rows: repeat(2, minmax(auto, 1fr));
    & > img {
      height: 10rem;
      margin: auto;
      grid-column: 1/-1;
      grid-row: 1/2;
      padding: 0.75rem;
      border-radius: 1.5rem;
      object-fit: contain;
    }

    & > div {
      grid-column: 1/-1;
      grid-row: 2/-1;
      display: flex;
      flex-direction: column;
      padding: 1rem 2.5rem;
    }
  }
`;

const TitleWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
`;

const AudioTitle = styled.h4`
  font-weight: 400;
  font-size: 1.5rem !important;
  width: 100%;
`; // need to be warped inside sanitized html

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-self: flex-end;
  margin: 0.5rem;
  flex-direction: row-reverse;
`;

const generateBars = (count: number) => {
  return Array.from({ length: count }, () => Math.random() * 30 + 10); // 10–40px
};

const calcBarsBaseonWindowWidth = (width: number) => {
  return width < 800 ? Math.floor(width / 7) : Math.floor(width / 11);
};

interface IAudioProps {
  data: {
    thumbnailUrl: string;
    name: string;
    description: string;
    document: { url: string; [key: string]: any };
    [key: string]: any;
  };
  width: number;
  setWidth: (width: number) => void;
}

const AudioView: FC<IAudioProps> = ({ data, width, setWidth }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [progress, setProgress] = useState(0);
  const [barHeights, setBarHeights] = useState(() =>
    generateBars(calcBarsBaseonWindowWidth(width))
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const stopAudio = () => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      const p = audio.currentTime / audio.duration;
      setProgress(p);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    return () => audio.removeEventListener('timeupdate', handleTimeUpdate);
  }, [audioRef.current]);

  useEffect(() => {
    const handleChangeWidth = () => {
      setWidth(window.innerWidth);
      setBarHeights(generateBars(calcBarsBaseonWindowWidth(window.innerWidth)));
    };

    window.addEventListener('resize', handleChangeWidth);
    return () => window.removeEventListener('resize', handleChangeWidth);
  }, []);

  const playedBars = Math.floor(progress * calcBarsBaseonWindowWidth(width));

  return (
    <>
      <AudioBanner>
        {data.thumbnailUrl && (
          <img src={`${data.thumbnailUrl}`} alt={data.name} />
        )}
        <div>
          <TitleWrapper>
            <AudioTitle>{data.name}</AudioTitle>
          </TitleWrapper>
          <div className="waveform">
            {barHeights.map((height, i) => (
              <div
                key={i}
                className={`bar ${i < playedBars ? 'played' : 'unplayed'}`}
                style={{ height }}
              />
            ))}
          </div>
          <audio ref={audioRef} src={data.document.url} />
          <ButtonGroup>
            {isPlaying ? (
              <LocalIcon
                iconName={'pause-circle.svg'}
                onClick={togglePlayPause}
                color={black}
                isLink={true}
              />
            ) : (
              <LocalIcon
                iconName={'play-circle.svg'}
                onClick={togglePlayPause}
                color={black}
                isLink={true}
              />
            )}
            <LocalIcon
              iconName={'stop-circle.svg'}
              onClick={stopAudio}
              color={black}
              isLink={true}
            />
          </ButtonGroup>
        </div>
      </AudioBanner>
      {data?.description && (
        <Content>
          <PageCKEditor
            item={data}
            editing={false}
            updateContent={() => {
              /*console.log();*/
            }}
            saving={false}
            content={data.description}
          />
        </Content>
      )}
    </>
  );
};

export default AudioView;
